import { localizedNumber } from "../help/localization/localization";

const QUICK_AMOUNTS = [10, 25, 50];

export const formatPointsAmount = (
  amount: number,
  currency: string,
  isArabic: boolean
): string => {
  const rounded = Math.round(amount * 100) / 100;
  const value = localizedNumber(rounded);
  const separator = /^[A-Za-z]/.test(currency) ? " " : "";

  return isArabic
    ? `${value}${separator}${currency}`
    : `${currency}${separator}${value}`;
};

export const pointsToAmount = (
  points: number,
  redemptionRate: number
): number => Math.max(0, points * redemptionRate);

export const amountToPoints = (
  amount: number,
  redemptionRate: number
): number => {
  if (redemptionRate <= 0) {
    return 0;
  }

  return Math.ceil(amount / redemptionRate);
};

export const snapToIncrement = (
  points: number,
  redemptionIncrement = 1
): number => {
  if (!Number.isFinite(points) || points <= 0) {
    return 0;
  }

  const increment = redemptionIncrement > 0 ? redemptionIncrement : 1;

  return Math.floor(points - (points % increment));
};

export const minRedeemablePoints = (redemptionIncrement = 1): number =>
  redemptionIncrement > 0 ? redemptionIncrement : 1;

export const clampPoints = (
  points: number,
  maxPoints: number,
  redemptionIncrement = 1
): number => {
  const snapped = snapToIncrement(
    Math.min(Math.max(points, 0), maxPoints),
    redemptionIncrement
  );

  return Math.min(snapped, maxPoints);
};

export type PayWithPointsQuickOption = {
  amount?: number;
  points: number;
  isMax: boolean;
};

export const buildQuickOptions = (
  maxPoints: number,
  maxAmount: number,
  redemptionRate: number,
  redemptionIncrement = 1
): PayWithPointsQuickOption[] => {
  if (maxPoints <= 0 || redemptionRate <= 0) {
    return [];
  }

  const options: PayWithPointsQuickOption[] = [];

  QUICK_AMOUNTS.forEach((amount) => {
    if (amount >= maxAmount) {
      return;
    }

    const requestedPoints = amountToPoints(amount, redemptionRate);
    const points = clampPoints(requestedPoints, maxPoints, redemptionIncrement);

    if (points > 0 && points === requestedPoints) {
      options.push({ amount, points, isMax: false });
    }
  });

  options.push({ points: maxPoints, isMax: true });

  return options;
};
